const { updateSyncErrors, touch } = require('redux-form/lib/actions').default;
import { change, SubmissionError } from 'redux-form';
import { gettext } from '@eventbrite/i18n';
import { CheckoutLoginPostMessageName } from '@eventbrite/checkout-postmessage-api';

import { showCaptcha } from './auth';
import {
    sendPasswordlessEmail,
    setShowPasswordlessModal,
} from './passwordless';

import { parseFormPostData } from '../api/utils';
import { hasSocialAccount } from '../utils';
import { getLogInFormData } from './utils';
import { sleep } from '../components/utils';
import { LOOKUP_ERROR_SPEC } from '../api';
import { logInUser as logInUserApi } from '../api/login';
import { showAuthNotification, redirectToSignUp, hideCaptcha } from './auth';
import { LOG_IN_FORM, CAPTCHA_FIELD } from '../constants';

import {
    GA_EVENTBRITE,
    GA_FACEBOOK,
    GA_APPLE,
    GA_GOOGLE,
    GA_AUTH_CHECKOUT_CATEGORY,
} from '../constants/analytics';

import {
    UNKNOWN_ERROR_MESSAGE,
    LOCKED_ACCOUNT_ERROR_MESSAGE,
    MUST_CHANGE_PASSWORD_ERROR_MESSAGE,
    SUSPENDED_ACCOUNT_ERROR_MESSAGE,
    NOT_EMAIL_ASSOCIATED,
    CREATE_ACCOUNT,
    PASSWORD_INCORRECT,
    LOGIN_SOFT_ACCOUNT,
    ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_SOCIAL_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_GOOGLE_APPLE_AND_FB_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_GOOGLE_AND_FB_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_APPLE_AND_GOOGLE_ERROR_MESSAGE,
    ACCOUNT_CONNECTED_TO_GOOGLE_ERROR_MESSAGE,
    VERIFICATION_REQUIRED_ERROR_MESSAGE,
} from '../constants/messages';

import {
    trackLoginClicked,
    trackLoginSuccessOneStep,
    trackPasswordIncorrect,
    trackAccountAlreadyConnectedWithSocials,
    trackHandleOnErrorMetric,
    trackLoginEventCheckout,
    trackLoginSuccessful,
    trackOpenPasswordlessModal,
} from './gaTracking';

import { notifyParent } from '../utils';
import { submitSuccessRedirect } from '../utils/security';
import { trackEmailPasswordLogin } from '../utils/statsig';
import { track } from '@eventbrite/datalayer-library';
import { HEAP_LOGIN_PASSWORD_INCORRECT } from '@eventbrite/auth-lib';

import {
    HeapTrackingLibrary,
    LOGIN_METHOD_EVENTBRITE,
} from '@eventbrite/auth-lib/';

export const SET_SHOW_FORGOT_PASSWORD = 'SET_SHOW_FORGOT_PASSWORD';
export const setShowForgotPassword = (show) => ({
    type: SET_SHOW_FORGOT_PASSWORD,
    payload: show,
});

/**
 * Catch the error and shows the specific error message to the user.
 */
export const handleOnErrorLogin =
    (dispatch, state, email, actionCategory = null) =>
    (parsedError) => {
        const { error } = parsedError;
        const {
            social: { socialSSOSettings, socialProvider },
            lookup: { socialConnected },
        } = state;
        const gaActionCategory = actionCategory ? actionCategory : null;

        switch (error) {
            case 'CAPTCHA_REQUIRED':
                //we need to dispatch an action to handle displaying the captcha, so this specific error
                //lives slightly outside of the standard error protocol. Have it here because proxying the
                //specific error for requiring the captcha to this point is cleaner than inserting dispatch
                //directly into the response handling itself
                dispatch(showCaptcha());
                trackHandleOnErrorMetric({
                    state,
                    message: error,
                });
                //once we are here in the response handling and have access to dispatch, we need to continue
                //passing along the error to redux form such that its lifecycle methods correctly represent the
                //failed submission state. We don't want to actually display a form error, as at this stage
                //showing the captcha
                throw new SubmissionError({
                    [CAPTCHA_FIELD]: gettext('Complete to proceed'),
                });

            case 'USER_PASSWORD_INVALID': // Invalid password
            case 'ACCESS_DENIED':
                trackPasswordIncorrect({
                    state,
                    gaActionCategory,
                    sourceProvider: socialProvider,
                });
                track({
                    eventName: HEAP_LOGIN_PASSWORD_INCORRECT,
                    eventData: {},
                });
                dispatch(
                    showAuthNotification({
                        message: PASSWORD_INCORRECT,
                    }),
                );
                dispatch(
                    updateSyncErrors(
                        LOG_IN_FORM,
                        LOOKUP_ERROR_SPEC['PASSWORD_INCORRECT'],
                    ),
                );
                dispatch(setShowForgotPassword(true));
                dispatch(touch(LOG_IN_FORM, 'password'));
                break;

            case 'USER_EMAIL_INVALID': // Email doesn't exist
                dispatch(
                    showAuthNotification({
                        message: NOT_EMAIL_ASSOCIATED,
                        onClick: redirectToSignUp,
                        buttonCTA: CREATE_ACCOUNT,
                    }),
                );
                break;

            case 'USER_HAS_NO_PASSWORD': // Soft account or account linked with social
                if (socialConnected) {
                    // Linked with social
                    if (
                        // Linked with Facebook and Apple And Google
                        hasSocialAccount({
                            socialSSOSettings,
                            signInMethods: socialConnected,
                            socialToCompare: [GA_FACEBOOK, GA_APPLE, GA_GOOGLE],
                        })
                    ) {
                        trackAccountAlreadyConnectedWithSocials({
                            state,
                            connectedSocials: [
                                GA_FACEBOOK,
                                GA_APPLE,
                                GA_GOOGLE,
                            ],
                        });
                        dispatch(
                            showAuthNotification({
                                message:
                                    ACCOUNT_CONNECTED_TO_GOOGLE_APPLE_AND_FB_ERROR_MESSAGE,
                            }),
                        );
                    } else if (
                        // Linked with Facebook and Google
                        hasSocialAccount({
                            socialSSOSettings,
                            signInMethods: socialConnected,
                            socialToCompare: [GA_FACEBOOK, GA_GOOGLE],
                        })
                    ) {
                        trackAccountAlreadyConnectedWithSocials({
                            state,
                            connectedSocials: [GA_FACEBOOK, GA_GOOGLE],
                        });
                        dispatch(
                            showAuthNotification({
                                message:
                                    ACCOUNT_CONNECTED_TO_GOOGLE_AND_FB_ERROR_MESSAGE,
                            }),
                        );
                    } else if (
                        // Linked with Google and Apple
                        hasSocialAccount({
                            socialSSOSettings,
                            signInMethods: socialConnected,
                            socialToCompare: [GA_APPLE, GA_GOOGLE],
                        })
                    ) {
                        trackAccountAlreadyConnectedWithSocials({
                            state,
                            connectedSocials: [GA_GOOGLE, GA_APPLE],
                        });
                        dispatch(
                            showAuthNotification({
                                message:
                                    ACCOUNT_CONNECTED_TO_APPLE_AND_GOOGLE_ERROR_MESSAGE,
                            }),
                        );
                    } else if (
                        // Linked with Facebook and Apple
                        hasSocialAccount({
                            socialSSOSettings,
                            signInMethods: socialConnected,
                            socialToCompare: [GA_FACEBOOK, GA_APPLE],
                        })
                    ) {
                        trackAccountAlreadyConnectedWithSocials({
                            state,
                            connectedSocials: [GA_FACEBOOK, GA_APPLE],
                        });
                        dispatch(
                            showAuthNotification({
                                message:
                                    ACCOUNT_CONNECTED_TO_APPLE_AND_FB_ERROR_MESSAGE,
                            }),
                        );
                    } else if (
                        // Linked with Google
                        hasSocialAccount({
                            socialSSOSettings,
                            signInMethods: socialConnected,
                            socialToCompare: [GA_GOOGLE],
                        })
                    ) {
                        trackAccountAlreadyConnectedWithSocials({
                            state,
                            connectedSocials: [GA_GOOGLE],
                        });
                        dispatch(
                            showAuthNotification({
                                message:
                                    ACCOUNT_CONNECTED_TO_GOOGLE_ERROR_MESSAGE,
                            }),
                        );
                    } else if (
                        // Linked with Facebook
                        hasSocialAccount({
                            socialSSOSettings,
                            signInMethods: socialConnected,
                            socialToCompare: [GA_FACEBOOK],
                        })
                    ) {
                        trackAccountAlreadyConnectedWithSocials({
                            state,
                            connectedSocials: [GA_FACEBOOK],
                        });
                        dispatch(
                            showAuthNotification({
                                message:
                                    ACCOUNT_CONNECTED_TO_FACEBOOK_ERROR_MESSAGE,
                            }),
                        );
                    } else if (
                        // Linked with Apple
                        hasSocialAccount({
                            socialSSOSettings,
                            signInMethods: socialConnected,
                            socialToCompare: [GA_APPLE],
                        })
                    ) {
                        trackAccountAlreadyConnectedWithSocials({
                            state,
                            connectedSocials: [GA_APPLE],
                        });
                        dispatch(
                            showAuthNotification({
                                message:
                                    ACCOUNT_CONNECTED_TO_APPLE_ERROR_MESSAGE,
                            }),
                        );
                    } else {
                        //It's linked with different social
                        dispatch(
                            showAuthNotification({
                                message:
                                    ACCOUNT_CONNECTED_TO_SOCIAL_ERROR_MESSAGE,
                            }),
                        );
                    }
                } else {
                    // Soft account
                    dispatch(
                        showAuthNotification({
                            message: LOGIN_SOFT_ACCOUNT.replace(
                                '{email}',
                                email,
                            ),
                        }),
                    );
                }
                break;

            case 'FORBIDDEN': // Account Locked
                dispatch(
                    showAuthNotification({
                        message: LOCKED_ACCOUNT_ERROR_MESSAGE,
                    }),
                );

                break;

            case 'USER_ACCOUNT_SUSPENDED': // Account suspended
                dispatch(
                    showAuthNotification({
                        message: SUSPENDED_ACCOUNT_ERROR_MESSAGE,
                    }),
                );
                break;

            case 'CREDENTIALS_MUST_CHANGE': // Password must be changed before login
                dispatch(
                    showAuthNotification({
                        message: MUST_CHANGE_PASSWORD_ERROR_MESSAGE,
                    }),
                );
                dispatch(setShowForgotPassword(true));
                break;

            case 'VERIFICATION_REQUIRED': // we must send passwordless email
                if (actionCategory === GA_AUTH_CHECKOUT_CATEGORY) {
                    dispatch(
                        showAuthNotification({
                            message: VERIFICATION_REQUIRED_ERROR_MESSAGE,
                        }),
                    );
                    trackHandleOnErrorMetric({
                        state,
                        message: error,
                    });
                } else {
                    dispatch(setShowPasswordlessModal(true));
                    dispatch(trackOpenPasswordlessModal(error));
                    dispatch(
                        sendPasswordlessEmail({
                            email,
                            isDirect: false,
                        }),
                    );
                }

                break;
            default:
                // Unknown error, let the user know and reject the promise
                dispatch(
                    showAuthNotification({
                        message: UNKNOWN_ERROR_MESSAGE,
                    }),
                );
                break;
        }
        return Promise.resolve();
    };
/**
 * Try to log in user using mail and password completed in  LogIn Form.
 */
export const logInUser = (data) => async (dispatch, getState) => {
    let state = getState();
    let {
        lookup: { lookupLoading },
    } = state;
    dispatch(setShowForgotPassword(false));
    /* istanbul ignore next */
    while (lookupLoading === true) {
        // wait until loading finish
        await sleep(1500);
        state = getState();
        lookupLoading = state.lookup.lookupLoading;
    }
    dispatch(hideCaptcha());
    dispatch(change(LOG_IN_FORM, CAPTCHA_FIELD, null));
    trackLoginClicked({ sourceProvider: GA_EVENTBRITE, state });

    return logInUserApi({
        data: parseFormPostData(getLogInFormData(data, getState)),
    })
        .then((response) => {
            trackLoginSuccessOneStep({
                sourceProvider: GA_EVENTBRITE,
                state,
            });
            trackEmailPasswordLogin();
            return dispatch(submitSuccessRedirect(response['redirect_url']));
        })
        .catch(handleOnErrorLogin(dispatch, state, data?.email));
};

export const logInCheckoutUser = (data) => (dispatch, getState) => {
    const state = getState();

    dispatch(hideCaptcha());
    dispatch(change(LOG_IN_FORM, CAPTCHA_FIELD, null));

    return logInUserApi({
        data: parseFormPostData(getLogInFormData(data, getState)),
    })
        .then((response) => {
            trackLoginEventCheckout({ state });
            trackLoginSuccessful({
                sourceProvider: GA_EVENTBRITE,
                state,
                actionCategory: GA_AUTH_CHECKOUT_CATEGORY,
            });
            HeapTrackingLibrary.trackLoginSuccessInHeap({
                loginMethod: LOGIN_METHOD_EVENTBRITE,
            });
            notifyParent(CheckoutLoginPostMessageName.checkoutLoginSuccessful);
        })
        .catch(
            handleOnErrorLogin(
                dispatch,
                state,
                data?.email,
                GA_AUTH_CHECKOUT_CATEGORY,
            ),
        );
};
